// FolderPickerModal — pick a destination folder. Used for bulk-move from
// the multi-select bar. Renders the folder list flat with full paths so
// the user can pick a deep destination without clicking through chevrons.

import React, { useMemo, useState } from 'react'
import { assetRegistry } from '../../lib/assetRegistry'
import { isHiddenFolderKey, showHiddenFolders } from '../../lib/hiddenFolders'

const C = {
  scrim:    'rgba(4, 6, 12, 0.78)',
  bg:       '#11131b',
  border:   '#2a2c36',
  text:     '#e1e1ed',
  dim:      '#8f8fa2',
  primary:  '#4d5bff',
  onPrim:   '#f7f4ff',
  hover:    '#1a1d28',
  selected: '#1f2a5c',
}

export default function FolderPickerModal({ title = 'Move to folder', onCancel, onPick }) {
  // Reserved dot-folders are not offered as move destinations (hiddenFolders.js).
  const folders = useMemo(
    () => assetRegistry.listFolders().filter(k => showHiddenFolders() || !isHiddenFolderKey(k)),
    [],
  )
  const [picked, setPicked] = useState([]) // path[]
  const [query, setQuery] = useState('')

  const items = folders
    .map(key => ({ key, path: key ? key.split('/') : [] }))
    .filter(x => !query || x.key.toLowerCase().includes(query.toLowerCase()))

  return (
    <div
      onMouseDown={e => { if (e.target === e.currentTarget) onCancel?.() }}
      style={{
        position: 'fixed', inset: 0, background: C.scrim,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div style={{
        background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6,
        width: 'min(440px, 90vw)', maxHeight: '70vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 18px 60px rgba(0,0,0,.55)',
      }}>
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${C.border}`,
          color: C.text, fontFamily: 'var(--m-font)', fontSize: 13, fontWeight: 700,
        }}>{title}</div>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Filter folders…"
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box',
              background: '#080a10', border: `1px solid ${C.border}`,
              borderRadius: 3, padding: '5px 10px',
              color: C.text, fontSize: 12, fontFamily: 'var(--m-font)', outline: 'none',
            }}
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 6 }}>
          {items.length === 0 ? (
            <div style={{ color: C.dim, fontFamily: 'var(--m-font)', fontSize: 11, textAlign: 'center', padding: 20 }}>
              No folders match.
            </div>
          ) : items.map(({ key, path }) => {
            const isSel = picked.join('/') === key
            return (
              <button
                key={key}
                onClick={() => setPicked(path)}
                onDoubleClick={() => onPick(path)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  background: isSel ? C.selected : 'transparent',
                  border: 'none', borderRadius: 3,
                  color: isSel ? C.text : C.dim,
                  padding: '6px 10px',
                  fontFamily: 'var(--m-font)', fontSize: 11,
                  cursor: 'pointer',
                }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = C.hover }}
                onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent' }}
              >
                /{key}
              </button>
            )
          })}
        </div>
        <div style={{
          padding: '12px 18px', borderTop: `1px solid ${C.border}`,
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          <button onClick={onCancel} style={btnSecondary}>Cancel</button>
          <button
            onClick={() => onPick(picked)}
            style={{ ...btnPrimary }}
          >Move</button>
        </div>
      </div>
    </div>
  )
}

const btnBase = {
  padding: '6px 14px', borderRadius: 4,
  fontFamily: 'var(--m-font)', fontSize: 11, fontWeight: 600,
  letterSpacing: '.06em', textTransform: 'uppercase',
  cursor: 'pointer',
}
const btnPrimary = { ...btnBase, background: C.primary, border: `1px solid ${C.primary}`, color: C.onPrim }
const btnSecondary = { ...btnBase, background: 'transparent', border: `1px solid ${C.border}`, color: C.dim }
